import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";
import { link } from "ionicons/icons";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}



const MatchSeasons: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current; console.log(containerRef)
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget); console.log('left position - ', position)
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget); console.log('right position - ', position)

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    const fetchQuestions = async () => {
        try {
            let data = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'MatchSeasons.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/MatchSeasons.json');
                data = module.default;
            }

            const shuffledData = shuffleArray(data);
            setTotalQuestion(shuffledData);
            setQuestions(shuffledData[currentIndex].pairs);
            setQuestion(shuffledData[currentIndex].question);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };


    useEffect(() => {
        fetchQuestions();
        document.title = "Seasons – Match the Images Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        resetConnections()
        setShowImage(true);
        setTimeout(() => {
            if (currentIndex != totalQuestion.length - 1) {
                setCurrentIndex(currentIndex + 1);
                console.log(totalQuestion[currentIndex + 1].pairs)
                setQuestions(totalQuestion[currentIndex + 1].pairs)
            }
            else {
                setCurrentIndex(0);
                fetchQuestions();
            }
            setShowImage(false);
        }, 3000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Download this UKG worksheet to match seasonal images like clothes, weather, and activities with the correct season. Fun, visual learning for kindergarten kids!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/seasons/match-images" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Seasons – Match the Images</h1>

                                <p>
                                    Learning about the seasons becomes exciting when kids engage with visuals! This <strong>Match the Images</strong> worksheet is designed for <strong>UKG (Upper Kindergarten)</strong> students to help them connect seasonal items, weather, and clothing with the correct season.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and identify the four main seasons: Summer, Winter, Monsoon, and Spring</li>
                                    <li>Associate clothing, weather, and natural elements with seasons</li>
                                    <li>Enhance observation and matching skills through visuals</li>
                                </ul>

                                <h2>📝 Worksheet Activity</h2>
                                <p>
                                    Children are shown a group of seasonal objects (e.g., umbrellas, ice creams, sweaters, flowers) and are asked to draw lines or match them to the correct season.
                                </p>

                                <h2>🖼️ Example Image Matching</h2>
                                <ul>
                                    <li>Snowman → Winter</li>
                                    <li>Umbrella → Monsoon</li>
                                    <li>Ice Cream → Summer</li>
                                    <li>Flowers Blooming → Spring</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Benefits of This Activity</h2>
                                <ul>
                                    <li>Visual learning aids memory retention</li>
                                    <li>Strengthens environmental awareness</li>
                                    <li>Improves cognitive association and motor skills</li>
                                    <li>Fun and interactive way to learn seasons</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Seasons – Match the Images</h1>

                            <p>
                                Learning about the seasons becomes exciting when kids engage with visuals! This <strong>Match the Images</strong> worksheet is designed for <strong>UKG (Upper Kindergarten)</strong> students to help them connect seasonal items, weather, and clothing with the correct season.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize and identify the four main seasons: Summer, Winter, Monsoon, and Spring</li>
                                <li>Associate clothing, weather, and natural elements with seasons</li>
                                <li>Enhance observation and matching skills through visuals</li>
                            </ul>

                            <h2>📝 Worksheet Activity</h2>
                            <p>
                                Children are shown a group of seasonal objects (e.g., umbrellas, ice creams, sweaters, flowers) and are asked to draw lines or match them to the correct season.
                            </p>

                            <h2>🖼️ Example Image Matching</h2>
                            <ul>
                                <li>Snowman → Winter</li>
                                <li>Umbrella → Monsoon</li>
                                <li>Ice Cream → Summer</li>
                                <li>Flowers Blooming → Spring</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Benefits of This Activity</h2>
                            <ul>
                                <li>Visual learning aids memory retention</li>
                                <li>Strengthens environmental awareness</li>
                                <li>Improves cognitive association and motor skills</li>
                                <li>Fun and interactive way to learn seasons</li>
                            </ul>
                        </article>
                        <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                            {/* SVG for drawing lines */}
                            <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                                {connections.map((conn, index) => (
                                    <line
                                        key={index}
                                        x1={conn.x1}
                                        y1={conn.y1}
                                        x2={conn.x2}
                                        y2={conn.y2}
                                        stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                        strokeWidth="3"
                                    />
                                ))}
                            </svg>


                            {/* <h6>{question}</h6> */}
                            {/* Left & Right columns under SVG */}
                            <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div
                                            key={item.left_id}
                                            className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                            onClick={(e) => handleLeftClick(e, item.left_id)}
                                            style={{ cursor: "pointer", padding: "10px" }}
                                        >
                                            <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + item.leftImage} alt="Left Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>

                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + item.rightImage} alt="Right Option" style={{ width: "100px", height: "100px" }} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className=" text-center" style={{ display: 'flex', gap: '10px' }}>
                                <div className="optionButton background-blue" style={{ fontSize: '18px' }} onClick={resetConnections}>
                                    Reset
                                </div>
                                <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next &gt;&gt;</div>
                            </div>

                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MatchSeasons;
